import { route } from 'preact-router';
import { useEffect, useState } from 'preact/hooks';
import { BASE_PATH, type EventRecord } from '../../shared/types';
import { useMe } from '../lib/auth';
import { api } from '../lib/api';
import { formatDates, registrationUrl } from '../lib/format';
import { unpublishedChanges } from '../lib/eventInput';
import { AccountMenu } from '../components/AccountMenu';
import { HelpLink } from '../components/HelpLink';
import { unitLabel } from '../../shared/slug';
import { AppIcon } from '../components/AppIcon';
import { EventMenu } from '../components/EventMenu';
import { Brand } from '../components/Brand';

export function Dashboard() {
  const { me } = useMe();
  const [events, setEvents] = useState<EventRecord[] | null>(null);
  useEffect(() => { if (me === null) route(`${BASE_PATH}/login`, true); }, [me]);
  useEffect(() => { if (me) api.get<EventRecord[]>('/events').then(setEvents); }, [me]);
  if (!me) return <main class="container"><p class="muted" role="status">Loading…</p></main>;
  return (
    <main class="container dashboard">
      <div class="dashboard-brandbar">
        <Brand />
        <div class="brandbar-actions"><HelpLink /><AccountMenu email={me.email} isAdmin={me.isAdmin} /></div>
      </div>
      <header class="dashboard-header">
        <div><p class="eyebrow">Registration, made simple.</p><h1>Your events</h1><p class="muted">A little less paperwork for your next activity.</p></div>
        {events && events.length > 0 && <a class="button" href={`${BASE_PATH}/events/new`}>Create a new event <AppIcon name="arrow" /></a>}
      </header>
      {events === null ? <p class="muted" role="status">Loading your events…</p> : events.length === 0 ? (
        <section class="dashboard-empty" aria-labelledby="first-event-title">
          <div class="dashboard-empty-icon"><AppIcon name="calendar" /></div>
          <h2 id="first-event-title">Your first event starts here.</h2>
          <p>Add the activity details, connect your Google account, and share one simple registration link.</p>
          <a class="button" href={`${BASE_PATH}/events/new`}>Create a new event <AppIcon name="arrow" /></a>
          <p class="dashboard-empty-note">Free to use. Your registrations stay in your Google Drive.</p>
        </section>
      ) : (
        <ul class="cards">
          {events.map((ev) => (
            <li class="card" key={ev.id}>
              <div class="row between event-card-header">
                <a href={`${BASE_PATH}/events/${ev.id}`}><strong>{ev.name || 'Untitled event'}</strong></a>
                <div class="event-card-actions"><span class="event-card-badges"><span class={`badge ${ev.status}`}>{ev.status === 'open' ? 'published' : ev.status}</span>
                  {!!unpublishedChanges(ev).length && <span class="badge changes">Unpublished changes</span>}</span>
                  <EventMenu eventId={ev.id} name={ev.name} onDeleted={() => setEvents((current) => current?.filter((event) => event.id !== ev.id) ?? null)} />
                </div>
              </div>
              <div class="muted">{ev.level === 'ward' ? `${unitLabel(ev.wardName ?? '', 'Ward')}, ` : ''}{unitLabel(ev.stakeName, 'Stake')} · {formatDates(ev.dates)}</div>
              <div class="muted small">
                {ev.status === 'open' ? <>
                <a href={registrationUrl(ev.path, ev.name, ev.dates)} target="_blank" rel="noopener">{registrationUrl(ev.path, ev.name, ev.dates)}</a>
                {' '}<button class="link" onClick={() => navigator.clipboard.writeText(registrationUrl(ev.path, ev.name, ev.dates))}>Copy registration link</button></>
                  : ev.status === 'closed' ? <>Registration closed · <a href={`${BASE_PATH}/events/${ev.id}?tab=review`}>Manage event</a></>
                  : <>Not published yet · <a href={`${BASE_PATH}/events/${ev.id}`}>Continue setup</a></>}
                {ev.google.spreadsheetId && <> · <a href={`https://docs.google.com/spreadsheets/d/${ev.google.spreadsheetId}`} target="_blank" rel="noopener">Open sheet</a></>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
